import { useState } from "react";
import { Mail } from "@/assets/icons/Mail";
import { Phone } from "@/assets/icons/Phone";
import { Address } from "@/assets/icons/Address";
import { useTranslation } from "react-i18next";
import { apiClient } from "@/api/apiClient";
import { toast } from "sonner";

interface FormData {
  name: string;
  phone: string;
  email: string;
  message: string;
}

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<FormData>({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validate = () => {
    const newErrors: Partial<FormData> = {};
    if (!formData.name.trim()) newErrors.name = t("validation.required");
    if (!formData.phone.trim()) {
      newErrors.phone = t("validation.required");
    } else if (!/^\+?[\d\s-]{8,20}$/.test(formData.phone)) {
      newErrors.phone = t("validation.invalid_phone");
    }
    if (!formData.email.trim()) {
      newErrors.email = t("validation.required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("validation.invalid_email");
    }
    if (!formData.message.trim()) newErrors.message = t("validation.required");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      await apiClient.post("/contact", formData);
      toast.success(t("contact.success_message"));
      setFormData({ name: "", phone: "", email: "", message: "" });
      setErrors({});
    } catch (error) {
      toast.error(t("contact.error_message"));
      console.error("Contact form error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <section id="contact">
      {/* Desktop */}
      <div className="hidden relative w-full max-w-360 mx-auto bg-white xl:flex items-stretch">
        <div className="w-3/4 pt-37.5 pb-10 pl-20 pr-100 flex flex-col gap-6 bg-white">
          <form onSubmit={handleSubmit} className="w-full max-w-171">
            <div className="flex flex-col gap-10">
              <div className="flex flex-col gap-2">
                <p className="text-4xl text-main-1">{t("contact.get_your")}</p>
                <p className="font-medium text-4xl text-main-1">
                  {t("contact.consultation")}
                </p>
              </div>

              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-1">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`h-12.5 p-6 border ${errors.name ? "border-red-500" : "border-main-1"}`}
                    placeholder={t("contact.name")}
                  />
                  {errors.name && (
                    <span className="text-red-500 text-xs px-1">
                      {errors.name}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`h-12.5 p-6 border ${errors.phone ? "border-red-500" : "border-main-1"}`}
                    placeholder={t("contact.phone")}
                  />
                  {errors.phone && (
                    <span className="text-red-500 text-xs px-1">
                      {errors.phone}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`h-12.5 p-6 border ${errors.email ? "border-red-500" : "border-main-1"}`}
                    placeholder={t("contact.email")}
                  />
                  {errors.email && (
                    <span className="text-red-500 text-xs px-1">
                      {errors.email}
                    </span>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className={`h-48 p-6 border ${errors.message ? "border-red-500" : "border-main-1"}`}
                    placeholder={t("contact.message")}
                  />
                  {errors.message && (
                    <span className="text-red-500 text-xs px-1">
                      {errors.message}
                    </span>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full h-12.5 bg-main-1 text-white text-base leading-5 rounded-none flex items-center justify-center hover:bg-main-1/90 transition-colors duration-300 cursor-pointer disabled:bg-gray-400"
                >
                  {isLoading ? t("contact.sending") : t("contact.send")}
                </button>
              </div>
            </div>
          </form>

          <div className="flex justify-between gap-4">
            <div className="flex gap-4 items-center">
              <Mail />
              <div className="flex flex-col gap-1">
                <p className="font-semibold text-sm text-black">
                  {t("contact.email")}
                </p>
                <a
                  href="mailto:monolitarmenia@gmail.com"
                  className="text-sm text-main-1 cursor-pointer"
                >
                  monolitarmenia@gmail.com
                </a>
              </div>
            </div>

            <div className="flex gap-4 items-center">
              <Phone />
              <div className="flex flex-col gap-1">
                <p className="font-semibold text-sm text-black">
                  {t("contact.phone")}
                </p>
                <a
                  href="tel:+37499050488"
                  className="text-sm text-main-1 cursor-pointer"
                >
                  +37499050488
                </a>
              </div>
            </div>

            <div className="flex gap-4 items-center">
              <Address />
              <div className="flex flex-col gap-1">
                <p className="font-semibold text-sm text-black">
                  {t("contact.address")}
                </p>
                <a
                  href={t("contact.map_link")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-main-1 cursor-pointer whitespace-nowrap"
                >
                  {t("contact.address_value")}
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="absolute w-full max-w-136 h-175 right-20 top-[50%] -translate-y-1/2 z-10">
          <iframe
            src={t("contact.map_url")}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
          ></iframe>
        </div>

        <div className="flex-1 mb-10 bg-main-1" />
      </div>

      {/* Mobile */}
      <div className="flex flex-col gap-15 xl:hidden">
        {/* 1. Form — white bg */}
        <div className="px-6 pt-8 pb-7 bg-white">
          <p className="text-2xl text-main-1">{t("contact.get_your")}</p>
          <p className="font-medium text-2xl text-main-1 mb-7">
            {t("contact.consultation")}
          </p>
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className={`h-12.5 px-6 border ${errors.name ? "border-red-500" : "border-main-1"}`}
                placeholder={t("contact.name")}
              />
              {errors.name && (
                <span className="text-red-500 text-[10px]">{errors.name}</span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={`h-12.5 px-6 border ${errors.phone ? "border-red-500" : "border-main-1"}`}
                placeholder={t("contact.phone")}
              />
              {errors.phone && (
                <span className="text-red-500 text-[10px]">{errors.phone}</span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`h-12.5 px-6 border ${errors.email ? "border-red-500" : "border-main-1"}`}
                placeholder={t("contact.email")}
              />
              {errors.email && (
                <span className="text-red-500 text-[10px]">{errors.email}</span>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                className={`h-30 p-6 border ${errors.message ? "border-red-500" : "border-main-1"}`}
                placeholder={t("contact.message")}
              />
              {errors.message && (
                <span className="text-red-500 text-[10px]">
                  {errors.message}
                </span>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-12.5 bg-main-1 text-white rounded-none disabled:bg-gray-400"
            >
              {isLoading ? t("contact.sending") : t("contact.send")}
            </button>
          </form>
        </div>

        <div className="w-full bg-main-1 px-5 pb-6 pt-16 mt-25">
          <div className="w-full h-100 -mt-50">
            <iframe
              src={t("contact.map_url")}
              width="100%"
              height="80%"
              style={{ border: 0 }}
              loading="lazy"
            />
          </div>
        </div>

        {/* 4. Contact info — white bg */}
        <div className="bg-white px-6 py-7 flex flex-col gap-5">
          <div className="flex items-center gap-3.5">
            <Mail />
            <div>
              <p className="text-sm font-semibold text-black">
                {t("contact.email")}
              </p>
              <a
                href="mailto:monolitarmenia@gmail.com"
                className="text-sm text-main-1"
              >
                monolitarmenia@gmail.com
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3.5">
            <Phone />
            <div>
              <p className="text-sm font-semibold text-black">
                {t("contact.phone")}
              </p>
              <a href="tel:+37499050488" className="text-sm text-main-1">
                +37499050488
              </a>
            </div>
          </div>

          <div className="flex items-center gap-3.5">
            <Address />
            <div>
              <p className="text-sm font-semibold text-black">
                {t("contact.address")}
              </p>
              <a
                href={t("contact.map_link")}
                target="_blank"
                className="text-sm text-main-1"
              >
                {t("contact.address_value")}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
